import React, {
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { Box, Button, Grid, Paper, Typography } from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import PageLayout from "@/components/layouts/page/PageLayout";
import NoDataFound from "@/components/NoDataFound";
import RpImageTextInline from "@/components/RpImageTextInline";
import ConfirmStripeEmbedCheckoutPopupBase from "@/components/stripe/embedCheckout/ConfirmStripeEmbedCheckoutPopup";
import { CurrencyFormateText } from "@/components/CurrencyFormat";
import { BRAND_CONST, HTTP_STATUS } from "@/config/constants";
import { DIRECT_MAIL_CAMP_PROVIDER } from "@/features/campaigns/direct-mail/utils/directMailConstants";
import { GlobalContext, LoaderContext } from "@/helpers/contexts/contexts";
import { logger } from "@/helpers/loggerHelper";
import { getDashboardPath } from "@/helpers/utils";
import { ROUTES } from "@/helpers/routes/routePaths";
import { grantThankYouAccessForFlow } from "@/shared/helpers/thankYouAccessHelper";
import { stripeMicroService } from "@/shared/services/stripeMicroService";
import { nationalPlusUpCampaignService } from "../services/nationalPlusUpCampaignService";
import {
  buildNationalPlusUpConfirmPayload,
  formatNationalMailingWeek,
} from "../utils/nationalPlusUpHelpers";

type NationalPlusUpRecord = Record<string, any>;

type NationalPlusUpRouteState = {
  campaignId?: string | number;
};

type SummaryDetail = {
  label: string;
  value: ReactNode;
};

type SummaryItemProps = {
  icon: string;
  label: string;
  value?: ReactNode;
  details?: SummaryDetail[];
  gridSize?: NationalPlusUpRecord;
  inlineValue?: boolean;
};

const ConfirmStripeEmbedCheckoutPopup =
  ConfirmStripeEmbedCheckoutPopupBase as React.ComponentType<
    Record<string, any>
  >;

function NationalPlusUpConfirmationPage() {
  const { selectedBrand, loggedInUser } = useContext(GlobalContext) as {
    selectedBrand?: NationalPlusUpRecord;
    loggedInUser?: NationalPlusUpRecord;
  };
  const loader = useContext(LoaderContext) as (_loading: boolean) => void;
  const navigate = useNavigate();
  const location = useLocation() as ReturnType<typeof useLocation> & {
    state?: NationalPlusUpRouteState;
  };
  const params = useParams();
  const campaignId = location.state?.campaignId || params.campaignId;

  const [draftData, setDraftData] = useState<NationalPlusUpRecord | null>(null);
  const [loading, setLoading] = useState(false);
  const [openPayment, setOpenPayment] = useState(false);

  const fetchDraftData = useCallback(
    async (id: string | number) => {
      try {
        setLoading(true);
        const res =
          await nationalPlusUpCampaignService.getDraftNationalPlusUpCampaign({
            campaignId: id,
            fkParticipantId: loggedInUser?.participantData?.id,
          });
        if (res.status === HTTP_STATUS.OK) {
          const data = res.data;
          if (String(data?.campaignId) === String(id)) {
            setDraftData(data);
          }
        }
      } catch (err) {
        logger.error("Fetch National Plus Up draft error", err);
      } finally {
        setLoading(false);
      }
    },
    [loggedInUser?.participantData?.id]
  );

  useEffect(() => {
    if (campaignId) {
      fetchDraftData(campaignId);
    }
  }, [campaignId, fetchDraftData]);

  useEffect(() => {
    loader(loading);
    return () => {
      loader(false);
    };
  }, [loading, loader]);

  if (
    selectedBrand?.id === BRAND_CONST.MS_BRAND_ID ||
    selectedBrand?.id === BRAND_CONST.KUMON_BRAND_ID
  ) {
    navigate(`${getDashboardPath()}`, { replace: true });
    return null;
  }

  if (!campaignId) {
    navigate(`${getDashboardPath()}`, { replace: true });
    return null;
  }

  const selections = draftData?.selections || [];
  const totalBudget = draftData?.totalBudget || 0;

  const dataToSave = (paymentStatus = 0) =>
    buildNationalPlusUpConfirmPayload({
      draftData,
      totalBudget,
      loggedInUser,
      selectedBrand,
      paymentStatus,
    });

  const handleFinalConfirm = async () => {
    let navigatedToThankYou = false;
    try {
      setLoading(true);
      const res =
        await nationalPlusUpCampaignService.confirmNationalPlusUpCampaignJoin(
          dataToSave()
        );
      if (res.status === HTTP_STATUS.OK) {
        navigatedToThankYou = true;
        setLoading(false);
        loader(false);
        grantThankYouAccessForFlow({
          stateFrom: "national-plus-up-confirm",
        });
        navigate(ROUTES.THANK_YOU, {
          replace: true,
          state: {
            from: "national-plus-up-confirm",
            campaignCat: "directmail",
            provider: DIRECT_MAIL_CAMP_PROVIDER.RRD,
          },
        });
      }
    } catch (err) {
      logger.error("National Plus Up final confirm error", err);
    } finally {
      if (!navigatedToThankYou) {
        setLoading(false);
      }
    }
  };

  const renderContent = () => {
    if (loading) return <NoDataFound message="Loading..." />;
    if (!draftData || String(draftData?.campaignId) !== String(campaignId)) {
      return <NoDataFound message="No campaign found!" />;
    }
    if (!selections.length) {
      return <NoDataFound message="No order found!" />;
    }

    return (
      <>
        <Typography
          component="h1"
          variant="h4"
          color="text.primary"
          gutterBottom
        >
          Confirmation
        </Typography>
        <Typography variant="caption" className="mb-5">
          Review your selections and make changes if required before you go to
          payment.
        </Typography>

        {selections.map((selection: NationalPlusUpRecord) => {
          const hasDistribution = hasDistributionSelected(selection);
          const hasStoreCopy = Boolean(selection.storeCopy);

          return (
            <Paper
              key={`${selection.locationId}-${selection.orderId || "draft"}`}
              elevation={2}
              sx={{ p: 3, mt: 3 }}
            >
              <Grid container spacing={3}>
                <SummaryItem
                  icon="location_mark"
                  label="Location Name"
                  value={selection.locationName}
                  gridSize={{ xs: 12, md: "grow" }}
                />
                <SummaryItem
                  icon="location_mark"
                  label="Market Name"
                  value={
                    selection.marketName ||
                    selection.market_name ||
                    selection.locationInfo?.market_name
                  }
                />
                <SummaryItem
                  icon="month_calender"
                  label="National Mailing Week"
                  value={formatNationalMailingWeek(
                    selection.mailingDate || selection.nationalMailingWeek
                  )}
                />
                <SummaryItem
                  icon="camera"
                  label="Plus-Up Distribution"
                  value={hasDistribution ? "Y" : "N"}
                  inlineValue
                  details={[
                    {
                      label: "Plus-Up Quantity:",
                      value: formatNumber(
                        hasDistribution
                          ? selection.estimatedDistribution ||
                              selection.plusUpQuantity
                          : 0
                      ),
                    },
                    {
                      label: "Plus-Up Cost:",
                      value: (
                        <>
                          <CurrencyFormateText
                            value={
                              hasDistribution
                                ? selection.distributionCost ||
                                  selection.plusUpCost ||
                                  0
                                : 0
                            }
                          />{" "}
                          USD
                        </>
                      ),
                    },
                  ]}
                />
                <SummaryItem
                  icon="cottage"
                  label="Store Copy"
                  value={hasStoreCopy ? "Y" : "N"}
                  inlineValue
                  details={[
                    {
                      label: "Store Copy Quantity:",
                      value: formatNumber(
                        hasStoreCopy ? selection.storeCopyQnt : 0
                      ),
                    },
                    {
                      label: "Store Copy Cost:",
                      value: (
                        <>
                          <CurrencyFormateText
                            value={
                              hasStoreCopy ? selection.storeCopyCost || 0 : 0
                            }
                          />{" "}
                          USD
                        </>
                      ),
                    },
                  ]}
                />
                <SummaryItem
                  icon="money"
                  label="Total Cost"
                  value={
                    <>
                      <CurrencyFormateText value={selection.totalCost || 0} />{" "}
                      USD
                    </>
                  }
                />
              </Grid>
            </Paper>
          );
        })}

        <Paper elevation={2} sx={{ p: 3, mt: 3 }}>
          <Grid container justifyContent="flex-end" spacing={4}>
            <Grid size="auto">
              <Typography variant="h6" color="text.primary" fontWeight={700}>
                Total You Pay:
              </Typography>
            </Grid>
            <Grid size="auto">
              <Typography variant="h6" color="text.primary" fontWeight={700}>
                <CurrencyFormateText value={totalBudget} />
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        <Grid container spacing={2} sx={{ mt: 4 }} alignItems="center">
          <Grid size={{ xs: 12, md: "grow" }}>
            <Typography variant="caption">
              Once 'Confirm' is selected, you can't make changes, your
              selections will be final for this campaign
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: "auto" }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: "auto" }}>
                <Button
                  variant="outlined"
                  color="primary"
                  fullWidth
                  sx={{ minWidth: { md: 210 } }}
                  onClick={() =>
                    navigate(
                      `/participant/join-national-plus-up-campaign/${campaignId}`,
                      {
                        replace: true,
                      }
                    )
                  }
                >
                  Make Changes
                </Button>
              </Grid>
              <Grid size={{ xs: 12, md: "auto" }}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  sx={{ minWidth: { md: 210 } }}
                  onClick={() => {
                    if (draftData?.paymentMethod?.toLowerCase() === "invoice") {
                      handleFinalConfirm();
                    } else {
                      setOpenPayment(true);
                    }
                  }}
                >
                  Confirm
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {openPayment ? (
          <ConfirmStripeEmbedCheckoutPopup
            makePayment={openPayment}
            title="Payment"
            data={{
              email: loggedInUser?.email,
              amount: totalBudget,
              fk_brand_id: selectedBrand?.id,
              system_generated_brand_id:
                selectedBrand?.system_generated_brand_id,
              fk_brand_name: selectedBrand?.brand_name,
              ...dataToSave(1),
            }}
            onClose={() => setOpenPayment(false)}
            return_uri={`${ROUTES.NATIONAL_PLUS_UP_JOIN_CAMPAIGN_RETURN}?join_campaign=true`}
            api={stripeMicroService.joinCampaignPayment}
          />
        ) : null}
      </>
    );
  };

  return (
    <PageLayout className="join-campaign-page postcard-container pt-1">
      {renderContent()}
    </PageLayout>
  );
}

const SummaryItem = ({
  icon,
  label,
  value,
  details = [],
  gridSize = { xs: 6, md: "grow" },
  inlineValue = false,
}: SummaryItemProps) => (
  <Grid size={gridSize}>
    <RpImageTextInline
      textProps={{ style: { display: "grid" } }}
      alignCenter={false}
      svgIcon={icon}
      imageProps={{ color: "primary" }}
      text={
        <>
          {inlineValue ? (
            <Typography variant="caption">
              <strong className="fw-b">{label}:</strong> {value || "N/A"}
            </Typography>
          ) : (
            <>
              <Typography variant="caption">{label}</Typography>
              <Typography
                className="title-val fw-b mt-2"
                component="p"
                variant="caption"
              >
                {value || "N/A"}
              </Typography>
            </>
          )}
          {details.length ? (
            <Box className="mt-2">
              {details.map((detail) => (
                <Typography
                  key={detail.label}
                  className="title-val mt-1 mt-md-2"
                  component="p"
                  variant="caption"
                >
                  <span className="fw-b">{detail.label}</span> {detail.value}
                </Typography>
              ))}
            </Box>
          ) : null}
        </>
      }
    />
  </Grid>
);

function formatNumber(value: number | string | null | undefined) {
  return (Number(value) || 0).toLocaleString("en-US");
}

function hasDistributionSelected(selection: NationalPlusUpRecord = {}) {
  return Boolean(selection.distribution ?? selection.plusUpDistribution);
}

export default NationalPlusUpConfirmationPage;
